import { css } from '@emotion/react';
import { CardAffinity, CardType } from '@mythos/game/CardTypes';
import { GameRatingDelta } from '@mythos/game/GameModel';
import {
  constructGameViewModelFromRenderedGame,
  GameViewModel,
} from '@mythos/game/GameViewModel';
import { nonNull } from '@mythos/utils/utils';
import invariant from 'invariant';
import { times } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';

import { emptyUserGameStats, UserGameStats } from '@mythos/api/UserStats';
import { dateDiff, timeAgo } from '@mythos/utils/dateUtils';
import { mapValues } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { CardCountsView } from './CardCountsView';
import { SymbolWithNumber } from './CardHelpers';
import { CardNameView } from './CardNameView';
import { BasicCardRenderer } from './CardView';
import {
  CardTypeToSymbolColor,
  ColorPalette,
  UIColorPalette,
} from './ColorPalette';
import { graphql, useFragment } from './generated/gql';
import { executeGQL, User, UserSummaryFragmentDoc } from './GraphQLHelpers';
import HoverCardStore from './HoverCardStore';
import { MEDIA_QUERY_MOBILE } from './mobile_ui/MStyles';
import { useMobile } from './MobileContext';
import { PieChart } from './StatsViews';
import Styles from './Styles';
import Symbols, { SymbolView } from './Symbols';
import UserHoverView from './UserHoverView';
import { FinishPosition } from './ViewUtilities';

const UserProfileQuery = graphql(`
  query getUserProfile($id: ID!) {
    user(id: $id) {
      ...UserSummary
      statsJSON
      games(first: 5) {
        edges {
          node {
            ...GameSummary
            renderedGame
          }
        }
      }
    }
  }
`);

const GameSummaryFragment = graphql(`
  fragment GameSummary on Game {
    id
    startTimestamp
    endTimestamp
    players {
      user {
        ...UserSummary
      }
      gameStatsJSON
    }
    ratingDeltas {
      userID
      score
      originalRating
      newRating
    }
  }
`);

export default function UserProfilePage() {
  const params = useParams();
  const navigate = useNavigate();
  const userID = params.userID!;

  const userProfile = useUserProfile(userID);

  if (!userProfile) {
    return <div>Loading...</div>;
  }

  const firstPickData = getMostFrequentFirstPick(
    userProfile.stats.firstPickCardNames,
  );
  const firstPickCard =
    firstPickData && userProfile.gameSummaries.length > 0
      ? Object.values(
          userProfile.gameSummaries[userProfile.gameSummaries.length - 1]
            ?.gameViewModel.cardsByID,
        ).find((card) => card.name === firstPickData.name)
      : undefined;

  return (
    <div css={UserProfileStyles.container}>
      <div css={UserProfileStyles.content}>
        <div css={UserProfileStyles.nameAndProfilePic}>
          <img
            src={userProfile.profilePictureURL}
            css={UserProfileStyles.profilePicture}
          />
          <div css={UserProfileStyles.nameContainer}>
            <h1 css={UserProfileStyles.nameAndProfilePic}>
              {userProfile.name}
            </h1>
            <h2 css={UserProfileStyles.subtitle}>{userProfile.subtitle}</h2>
          </div>
        </div>
        <ul css={UserProfileStyles.stats}>
          <h2 css={UserProfileStyles.statsTitle}>Total stats</h2>
          <StatLine label="Games played">
            {userProfile.stats.gamesPlayed}
          </StatLine>
          <StatLine label="Games won">{userProfile.stats.gamesWon}</StatLine>
          <StatLine label="Rating">
            {userProfile.rating ? userProfile.rating : 'N/A'}
          </StatLine>
          <br />
        </ul>
        <ul css={UserProfileStyles.stats}>
          <h2 css={UserProfileStyles.statsTitle}>Average stats</h2>
          <StatLine label="Score">
            {formatScore(userProfile.averageStats.score)}
          </StatLine>
          <StatLine label="Score from cards">
            {formatScore(userProfile.averageStats.scoreFromCards)}
          </StatLine>
          <StatLine label="Score from prophecies">
            {formatScore(userProfile.averageStats.scoreFromProphecies)}
          </StatLine>
          <StatLine label="Score from tributes">
            {formatScore(userProfile.averageStats.scoreFromTributes)}
          </StatLine>
          <br />
          <StatLine label="Blame time">
            {formatTime(userProfile.averageStats.blameMillis)}
          </StatLine>
          <StatLine label="Solo thinking time">
            {formatTime(userProfile.averageStats.soloMillis)}
          </StatLine>
          <br />
          <StatLine label="Clashes">
            {formatCount(userProfile.averageStats.clashes)}
          </StatLine>
          <StatLine label="Clashes won">
            {formatCount(userProfile.averageStats.clashesWon)}
          </StatLine>
          <br />
          <li>
            <PieChart
              title="Card count by affinity"
              data={[
                {
                  name: CardAffinity.Derion,
                  count: formatCount(
                    userProfile.averageStats.cardCountByAffinity[
                      CardAffinity.Derion
                    ]!,
                  ),
                },
                {
                  name: CardAffinity.Tyros,
                  count: formatCount(
                    userProfile.averageStats.cardCountByAffinity[
                      CardAffinity.Tyros
                    ]!,
                  ),
                },
                {
                  name: CardAffinity.Zeona,
                  count: formatCount(
                    userProfile.averageStats.cardCountByAffinity[
                      CardAffinity.Zeona
                    ]!,
                  ),
                },
              ]}
              colors={[
                ColorPalette.derionGreen,
                ColorPalette.tyrosRed,
                ColorPalette.zeonaBlue,
              ]}
            />
          </li>
          <li>
            <PieChart
              title="Card count by type"
              data={Object.entries(
                userProfile.averageStats.cardCountByType,
              ).map(([key, value]) => ({
                name: key,
                count: formatCount(value),
              }))}
              colors={Object.entries(
                userProfile.averageStats.cardCountByType,
              ).map(([key, value]) => CardTypeToSymbolColor[key as CardType])}
            />
          </li>
        </ul>
        <ul css={UserProfileStyles.stats}>
          <h2 css={UserProfileStyles.statsTitle}>Other Stats</h2>
          <StatLine label="Most Frequent First Pick">
            {firstPickCard ? (
              <CardNameView card={firstPickCard} renderer={BasicCardRenderer} />
            ) : firstPickData ? (
              firstPickData.name
            ) : (
              'None'
            )}{' '}
            {firstPickData ? `(${firstPickData.count})` : ''}
          </StatLine>
        </ul>
        <br />
        <div css={UserProfileStyles.disclaimer}>More graphs to come!</div>

        <ul css={UserProfileStyles.gameHistoryList}>
          <h2 css={UserProfileStyles.statsTitle}>Recent games</h2>
          {userProfile.gameSummaries.map((game) => (
            <GameHistoryItem
              key={game.id}
              game={game}
              viewerID={userProfile.id}
              onClick={(game) => navigate(`/game/${game.id}`)}
            />
          ))}
        </ul>
        {HoverCardStore.getHoverCardComponent()}
      </div>
    </div>
  );
}

function GameHistoryItem(props: {
  game: GameSummary;
  onClick?: (game: GameSummary) => void;
  viewerID: string;
}) {
  const isMobile = useMobile();

  const viewerPlayer = props.game.players.find(
    (player) => player.user.id === props.viewerID,
  );
  invariant(viewerPlayer, 'Viewer player not found');

  const sortedPlayers = props.game.players.sort(
    (a, b) => b.stats.score - a.stats.score,
  );
  const sortedOpponents = sortedPlayers.filter(
    (player) => player.user.id !== props.viewerID,
  );

  const ratingDeltaObj = props.game.ratingDeltas?.find(
    (x) => x.userID === props.viewerID,
  );
  const ratingDelta = ratingDeltaObj
    ? ratingDeltaObj.newRating - ratingDeltaObj.originalRating
    : null;

  const viewerRank =
    sortedPlayers.findIndex((x) => x.user.id === props.viewerID) + 1;

  return (
    <li
      key={props.game.id}
      css={GameHistoryItemStyles.container}
      onClick={() => props.onClick?.(props.game)}
    >
      <div css={GameHistoryItemStyles.leftColumn}>
        <div css={Styles.flexRow}>
          <FinishPosition
            css={GameHistoryItemStyles.finishPosition}
            position={viewerRank}
          />
          <div css={GameHistoryItemStyles.ratingDelta}>
            {ratingDelta
              ? ` (${ratingDelta > 0 ? '+' : ''}${ratingDelta})`
              : ' -'}
          </div>
        </div>
        <div css={GameHistoryItemStyles.leftColumnSubItem}>
          {props.game.players.length}p Casual
        </div>
        <div css={GameHistoryItemStyles.leftColumnSubItem}>
          {timeAgo(props.game.endTimestamp)}
        </div>
        <div css={GameHistoryItemStyles.leftColumnSubItem}>
          {dateDiff(
            props.game.endTimestamp,
            props.game.startTimestamp,
            'minutes',
          )}
          m
        </div>
      </div>
      <div css={GameHistoryItemStyles.selfFavorContainer}>
        <SymbolWithNumber
          symbol={Symbols.FAVOR}
          number={viewerPlayer.stats.score}
          height={isMobile ? 24 : 30}
          fontSize={isMobile ? 12 : 16}
        />
        <div css={GameHistoryItemStyles.favorBreakdownContainer}>
          <div css={Styles.flexRow}>
            <SymbolView
              symbol={Symbols.TOKEN_TRIBUTE}
              css={GameHistoryItemStyles.opponentFavorBreakdownSymbol}
            />
            {viewerPlayer.stats.scoreFromTributes}
          </div>
          <div css={Styles.flexRow}>
            <SymbolView
              symbol={Symbols.CARD_BACK}
              css={GameHistoryItemStyles.opponentFavorBreakdownSymbol}
            />
            {viewerPlayer.stats.scoreFromCards}
          </div>
          <div css={Styles.flexRow}>
            <SymbolView
              symbol={Symbols.EOG}
              css={GameHistoryItemStyles.opponentFavorBreakdownSymbol}
            />
            {viewerPlayer.stats.scoreFromProphecies}
          </div>
        </div>
      </div>
      <div css={Styles.flexRow}>
        <CardCountsView
          game={props.game.gameViewModel}
          hideUpgradeIndicators
          // hideProphecyIndicators
          player={
            props.game.gameViewModel.playerStates.find(
              (x) => x.userID === props.viewerID,
            )!
          }
        />
      </div>
      <div css={GameHistoryItemStyles.opponentsContainer}>
        {sortedOpponents.map((player) => {
          const ratingDeltaObj = props.game.ratingDeltas?.find(
            (x) => x.userID === player.user.id,
          );
          const ratingDelta = ratingDeltaObj
            ? ratingDeltaObj.newRating - ratingDeltaObj.originalRating
            : null;
          return (
            <UserHoverView key={player.user.id} user={player.user}>
              <div css={GameHistoryItemStyles.opponentContainer}>
                <SymbolWithNumber
                  symbol={Symbols.FAVOR}
                  number={player.stats.score}
                  height={18}
                  fontSize={10}
                />
                <img
                  src={player.user.profilePictureURL}
                  css={GameHistoryItemStyles.opponentProfilePicture}
                />
                <div>{player.user.name} </div>
                <div css={GameHistoryItemStyles.ratingDelta}>
                  {ratingDelta
                    ? `${ratingDelta > 0 ? '+' : ''}${ratingDelta}`
                    : ''}
                </div>
              </div>
            </UserHoverView>
          );
        })}
        {times(4 - sortedOpponents.length, (i) => (
          <div
            key={`empty-${i}`}
            css={GameHistoryItemStyles.opponentContainer}
          />
        ))}
      </div>
    </li>
  );
}

const GameHistoryItemStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexGrow: 1,
    padding: 4,
    borderBottom: `1px solid ${UIColorPalette.gameHistoryItemSeparator}`,
    gap: 5,
  }),
  finishPosition: css({
    fontSize: '1.2em',
  }),
  ratingDelta: css({
    fontSize: '0.8em',
    color: UIColorPalette.gameHistoryItemSeparator,
    marginLeft: 2,
  }),
  opponentContainer: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',

    width: 150,
    height: 22,

    [MEDIA_QUERY_MOBILE]: {
      fontSize: '0.75em',
      width: 110,
      height: 18,
    },
  }),
  leftColumn: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minWidth: 60,
  }),
  leftColumnSubItem: css({
    fontSize: '0.8rem',
    color: UIColorPalette.gameHistoryItemSeparator,
  }),
  opponentFavorBreakdownSymbol: css({
    width: 14,
    height: 14,
    marginRight: 2,
    [MEDIA_QUERY_MOBILE]: {
      width: 9,
      height: 9,
      fontSize: '0.7em',
    },
  }),
  opponentProfilePicture: css({
    width: 18,
    height: 18,
    padding: 2,
    marginLeft: 4,
    [MEDIA_QUERY_MOBILE]: {
      marginLeft: 2,
      width: 14,
      height: 14,
    },
  }),
  opponentsContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }),
  favorBreakdownContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    color: UIColorPalette.gameHistoryItemSeparator,
    [MEDIA_QUERY_MOBILE]: {
      flexDirection: 'column',
      fontSize: '0.6em',
    },
  }),
  selfFavorContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,

    [MEDIA_QUERY_MOBILE]: {
      justifyContent: 'flex-start',
    },
  }),
};

function StatLine(props: { label: ReactNode; children: ReactNode }) {
  return (
    <li css={UserProfileStyles.statLine}>
      <span>{props.label}</span>
      <span>{props.children}</span>
    </li>
  );
}

const UserProfileStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }),
  content: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    backgroundColor: ColorPalette.lightBG,
    borderRadius: 10,
    padding: 8,
    maxWidth: 600,
    minWidth: 350,
    width: '100%',
    boxSizing: 'border-box',
  }),
  nameContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  }),
  nameAndProfilePic: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    fontSize: '2rem',
    fontWeight: 'bold',
  }),
  subtitle: css({
    fontSize: '1.2rem',
    fontWeight: 'normal',
    color: UIColorPalette.gameHistoryItemSeparator,
  }),
  stats: css({
    listStyle: 'none',
    padding: 8,
    margin: 0,
    marginTop: 10,
  }),
  statsTitle: css({
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: 10,
  }),
  profilePicture: css({
    width: 50,
    height: 50,
  }),
  statLine: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexGrow: 1,
  }),
  disclaimer: css({
    fontSize: '1.5rem',
    color: UIColorPalette.gameHistoryItemSeparator,
    alignSelf: 'center',
  }),
  gameHistoryList: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  }),
};

type CardPickData = {
  name: string;
  count: number;
};
function getMostFrequentFirstPick(
  firstPickCardNames: Record<string, number>,
): CardPickData | null {
  const entries = Object.entries(firstPickCardNames);
  if (entries.length === 0) return null;
  const mostFrequent = entries.reduce((a, b) => (a[1] > b[1] ? a : b));
  return {
    name: mostFrequent[0],
    count: mostFrequent[1],
  };
}

function formatTime(millis: number) {
  return `${Math.round(millis / 1000)}s`;
}
function formatScore(score: number) {
  return score.toFixed(2);
}
function formatCount(count: number) {
  return Math.round(count * 100) / 100;
}

type GameSummary = {
  id: string;
  startTimestamp: number;
  endTimestamp: number;
  players: PlayerSummary[];
  gameViewModel: GameViewModel;
  ratingDeltas: GameRatingDelta[];
};
type PlayerSummary = {
  user: User;
  stats: UserGameStats;
};

type UserProfile = {
  id: string;
  name: string;
  subtitle: string;
  profilePictureURL: string;
  rating: number | null;
  stats: UserGameStats;
  averageStats: UserGameStats;

  gameSummaries: GameSummary[];
};
function useUserProfile(userID: string): UserProfile | null {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  useEffect(() => {
    executeGQL(UserProfileQuery, { id: userID }).then((response) => {
      if (!response.data?.user) {
        return;
      }
      const user = response.data.user;
      const stats = user.statsJSON
        ? JSON.parse(user.statsJSON)
        : emptyUserGameStats();
      let averageStats: UserGameStats;
      if (stats.gamesPlayed === 0) {
        averageStats = stats;
      } else {
        averageStats = {
          gamesPlayed: stats.gamesPlayed / stats.gamesPlayed,
          gamesWon: stats.gamesWon / stats.gamesPlayed,
          blameMillis: stats.blameMillis / stats.gamesPlayed,
          soloMillis: stats.soloMillis / stats.gamesPlayed,
          score: stats.score / stats.gamesPlayed,
          scoreFromCards: stats.scoreFromCards / stats.gamesPlayed,
          scoreFromProphecies: stats.scoreFromProphecies / stats.gamesPlayed,
          scoreFromTributes: stats.scoreFromTributes / stats.gamesPlayed,
          clashes: stats.clashes / stats.gamesPlayed,
          clashesWon: stats.clashesWon / stats.gamesPlayed,

          cardCountByAffinity: mapValues(
            stats.cardCountByAffinity,
            (value) => value / stats.gamesPlayed,
          ),
          cardCountByType: mapValues(
            stats.cardCountByType,
            (value) => value / stats.gamesPlayed,
          ),
          firstPickCardNames: mapValues(
            stats.firstPickCardNames,
            (value) => value / stats.gamesPlayed,
          ),
        };
      }

      const userSummary = useFragment(UserSummaryFragmentDoc, user);

      setUserProfile({
        id: userSummary.id,
        name: userSummary.name,
        subtitle: userSummary.subtitle,
        profilePictureURL: userSummary.profilePictureURL,
        rating: userSummary.stats?.rating ?? null,
        stats,
        averageStats,
        gameSummaries:
          user.games?.edges
            ?.map((edge) => {
              const game = useFragment(GameSummaryFragment, edge?.node);
              if (!game) {
                return null;
              }
              return {
                id: game.id,
                startTimestamp: game.startTimestamp,
                endTimestamp: game.endTimestamp!,
                players: game.players.map(
                  (player) =>
                    ({
                      user: player.user,
                      stats: JSON.parse(player.gameStatsJSON!),
                    }) as PlayerSummary,
                ),
                ratingDeltas: game.ratingDeltas!,
                gameViewModel: constructGameViewModelFromRenderedGame(
                  JSON.parse(edge!.node!.renderedGame),
                ),
              };
            })
            .filter(nonNull) ?? [],
      });
    });
  }, [userID]);

  return userProfile;
}
